import React, { useMemo, useRef } from 'react'
import { bindActionCreators } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import styles from './ResponseTools.css'
import ReplyIcon from 'icons/ResponsesIcon'
import { loadReactions, updateReaction } from 'state/actions/reactions'
import { concludePost, unpublishPost } from 'state/actions/posts'


import OverflowMenu from 'components/OverflowMenu'
import { isAdmin, isAuthor } from 'posts/isAdmin'
import { useCircleMembership } from 'circle/circle.selectors'
import { errorsReported } from 'toasts/AppError'
import { editPostUrl, url4post } from 'circle/places'
import { useHistory } from 'react-router'
import { CommentPost } from 'posts/posts.types'
import { NotesMenuItem } from 'post-notes/NotesButton'
import styled from 'styled-components'
import { PostVoteButton } from 'post-ratings/PostVoteButton'
import { usePostContext } from 'posts/PostContext'
import { useResponseApi } from 'posts/useResponseApi'
import { useRefreshPostResponses } from 'posts/posts.selectors'
import { useResponsesContext } from 'post-responses/ResponsesContext'
import { ReactFC } from 'types'
import MenuList from '@material-ui/core/MenuList'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import { Divider, IconButton, Tooltip } from '@material-ui/core'
import { ShareIconButton } from './ShareDialog'
import { ReactIconButton } from './ReactionsDialog'
import { useIsLoggedIn } from 'profile/profile.selectors'
import { Post, SimplePlayer } from 'lib-core'
import { FlagPostMenuItem, UnFlagPostMenuItem } from 'posts/PostsOverflow'

export interface ResponseMenuProps {
  response: Post
  refresh,
}
const ResponseMenu: ReactFC<ResponseMenuProps> = ({ response, refresh }) => {
  const membership = useCircleMembership()
  const history = useHistory()
  const {
    profile,
    post,
  } = useSelector(selectForResponseTools)

  const {
    concludePost,
    onUnpublishPost,
  } = useResponseAPI()

  const overflowMenuVisible = profile /*&& (isAuthor(response, profile) || isAdmin(membership) || isAuthor(post, profile))*/ && !post.completed && !response.unpublished

  if (!overflowMenuVisible) {
    return null
  }

  const concludeDiscussion = errorsReported(() => {
    const { content, _id } = response

    const conclusion = Array.isArray(content) ? content : {
      nsId: _id,
      ...content,
    }

    const concludingPost = {
      post: post._id,
      conclusion: true,
      concludingPost: JSON.stringify(conclusion),
    }
    return concludePost(concludingPost)
  })

  const handleUnpublishAsAdmin = (target) => {
    handleUnpublish({ ...target, as_admin: true })
  }

  const handleUnpublish = async (target) => {
    try {
      await onUnpublishPost(target)
      refresh()
    } catch (err) {
      console.error(err)
    }
  }

  const openEditor = (target) => {
    history.push(editPostUrl(target))
  }

  const renderOverflowMenu: ReactFC<{ target: CommentPost }> = ({ target }) => {
    const items = {}

    if (isAdmin(membership)) {
      items["close"] = (
        <MenuItem
          key="close"
          onClick={concludeDiscussion}
        >
          <FormattedMessage id="CloseDiscussion" />
        </MenuItem>
      )
    }

    if (isAuthor(target, profile)) {
      items["unpublish"] = (
        <MenuItem
          key="unpublish"
          onClick={() => handleUnpublish(target)}
        >
          <FormattedMessage id="posts.unpublish" />
        </MenuItem>
      )

      items["posts.edit"] = (
        <MenuItem
          key="posts.edit"
          onClick={() => openEditor(target)}
        >
          <FormattedMessage id="posts.edit" />
        </MenuItem>
      )
    }

    if (isAdmin(membership)) {
      items["unpublish"] = (
        <MenuItem
          key="unpublish"
          onClick={() => handleUnpublishAsAdmin(target)}
        >
          <FormattedMessage id="posts.unpublish" />
        </MenuItem>
      )

      if (target.authorGroupRole === "ADMIN") {
        items["posts.edit"] = (
          <MenuItem
            key="posts.edit"
            onClick={() => openEditor(target)}
          >
            <FormattedMessage id="posts.edit" />
          </MenuItem>
        )
      }
    }

    const openResponse = () => {
      const url = url4post(response)
      window.open(url, "_blank")
    }

    items["posts.open_thread"] = (
      <MenuItem
        key="posts.open_thread"
        onClick={openResponse}
      >
        <FormattedMessage id="posts.open_thread" />
      </MenuItem>
    )

    return (
      <MenuList>
        {Object.values(items)}
      </MenuList>
    )
  }

  return (
    <span data-intro="response-menu">
      <OverflowMenu fn={renderOverflowMenu} target={response} contentComponent={MenuContent} />
    </span>
  )
}

const MenuContent = ({ fn: Component, target, onClick }) => {
  return (
    <Paper onClick={onClick}>
      <NotesMenuItem />
      <ShareIconButton />
      <FlagPostMenuItem response={target} />
      <UnFlagPostMenuItem response={target} />
      <Divider />
      <Component target={target} />
    </Paper>
  )
}

const CommentToolsWrapper = styled.div`
  position: relative;
  font-size: 2em;
  display: flex;
  flex-direction: row;
  color: #4d4d4d;
  padding: 0em;
  min-width: 125px;
  margin-left: auto;
  justify-content: flex-end;
  align-content: center;
  && .MuiIconButton-root, && .MuiToggleButton-root {
    padding: 4px;
  }
`

const ResponseTools = () => {
  const { showMenu } = useResponsesContext()

  return useMemo(() => {
    if (!showMenu) {
      return null
    }
    return <ResponseToolsInner />
  }, [showMenu])
}

const ReplyIconButton = () => {
  const isLoggedIn = useIsLoggedIn()

  const { prepareDraft } = useResponseApi()
  const { post } = useSelector(selectForResponseTools)
  const { setResponding, post: response } = usePostContext()

  if (!isLoggedIn || post.completed || response.unpublished) {
    return null
  }

  const onReplyClick = async () => {
    prepareDraft({
      responseTo: response._id,
    })
    setResponding(true)
  }

  return (
    <Tooltip title="Respond" aria-label="Respond">
      <IconButton
        aria-label="show members"
        color="inherit"
      >
        <ReplyIcon onClick={onReplyClick} data-intro="comment-response" />
      </IconButton>
    </Tooltip>
  )
}

const ResponseToolsInner = () => {
  const element = useRef<any>()
  const { post: response } = usePostContext()

  const refresh = useRefreshPostResponses()

  return (
    <div className={styles.response_tools} ref={element}>
      <CommentToolsWrapper>
        {response.audio && <SimplePlayer path={`${response._id}/audio`} bucket={response.audio} />}
        <ReplyIconButton />
        <PostVoteButton />
        <ResponseMenu
          response={response}
          refresh={refresh}
        />

      </CommentToolsWrapper>
    </div>
  )
}

export const selectForResponseTools = (
  {
    session: { value: profile },
    reactions: { map: reactions },
    post: { value: post },
    instance: {
      value: { domainName }
    }
  },
) => {
  return {
    domainName,
    profile,
    post,
    reactions,
  }
}

export const useResponseAPI = () => {
  const dispatch = useDispatch()
  return {
    loadReactions: bindActionCreators(loadReactions, dispatch),
    onUnpublishPost: bindActionCreators(unpublishPost, dispatch),
    updateReaction: bindActionCreators(updateReaction, dispatch),
    concludePost: bindActionCreators(concludePost, dispatch),
  }
}

export default ResponseTools

